import { useTheme } from '@/components/ThemeProvider';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DASHBOARD_STATS_FIELDS } from '@/graphql/fragments/dashboardFragments';
import { gql } from '@apollo/client';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { ResponsivePie } from '@nivo/pie';
import { AlertTriangle, ArrowUp, BadgeCheck, BarChart, CalendarDays, CheckCheck, ClipboardCheck, Clock, FileText, TrendingUp } from 'lucide-react';

const GET_DASHBOARD_STATS = gql`
   ${DASHBOARD_STATS_FIELDS}
   query GetDashboardStats {
      getDashboardStats {
      ...DashboardStatsFields
      }
   }
`;

const ApplicationAnalytics = ({ data }) => {
   const { theme } = useTheme();
   const isDark = theme === 'dark';

   const {
      applicationTypes,
      statusData,
      weeklyVolume
   } = data;

   // Calculate total applications
   const totalApplications = applicationTypes?.reduce((sum, item) => sum + item.value, 0) || 0;

   // Get the application status counts
   const submittedCount = statusData?.find(item => item.id === 'Submitted')?.value || 0;
   const inProgressCount = statusData?.find(item => item.id === 'In Progress')?.value || 0;
   const returnedCount = statusData?.find(item => item.id === 'Returned')?.value || 0;
   const completedCount = statusData?.find(item => item.id === 'Completed')?.value || 0;

   // Calculate percentages
   const submittedPercent = totalApplications ? Math.round((submittedCount / totalApplications) * 100) : 0;
   const inProgressPercent = totalApplications ? Math.round((inProgressCount / totalApplications) * 100) : 0;
   const returnedPercent = totalApplications ? Math.round((returnedCount / totalApplications) * 100) : 0;
   const completedPercent = totalApplications ? Math.round((completedCount / totalApplications) * 100) : 0;

   // Most frequent application type (FIX: Create a copy before sorting)
   const mostFrequentType = applicationTypes
        ? [...applicationTypes].sort((a, b) => b.value - a.value)[0]
        : null;

   // Generate processing time data for visualization
   const processingTimeData = [
      { id: 'CSAW', value: 4.2 },
      { id: 'PLTCP', value: 5.6 },
      { id: 'PTPR', value: 3.8 },
      { id: 'COV', value: 2.9 },
      { id: 'TCEBP', value: 6.7 },
      { id: 'PLTP', value: 4.1 }
   ];

   // Generate trend data
   const trendData = [
      {
        id: "Applications",
        data: [
          { x: "Week 1", y: 20 },
          { x: "Week 2", y: 32 },
          { x: "Week 3", y: 28 },
          { x: "Week 4", y: 41 },
          { x: "Week 5", y: 37 },
          { x: "Week 6", y: 43 },
          { x: "Current", y: totalApplications }
        ]
      }
    ];

   const hasApplicationTypes = applicationTypes?.length > 0;
   const hasStatusData = statusData?.length > 0;
   const hasWeeklyVolume = weeklyVolume?.some(day => day.count > 0);

   const commonPieProps = {
      margin: { top: 40, right: 80, bottom: 100, left: 80 },
      innerRadius: 0.5,
      padAngle: 0.7,
      cornerRadius: 3,
      activeOuterRadiusOffset: 8,
      borderWidth: 1,
      arcLinkLabelsSkipAngle: 10,
      arcLinkLabelsTextColor: isDark ? "#ffffff" : "#333333",
      arcLabelsSkipAngle: 10,
      theme: {
         legends: {
            text: {
               fill: isDark ? "#ffffff" : "#999999"
            }
         },
         tooltip: {
            container: {
               background: isDark ? "#1f2937" : "#ffffff",
               color: isDark ? "#ffffff" : "#333333",
            }
         }
      }
   };

   const EmptyState = ({ message }) => (
      <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
         <p className="text-lg font-medium">{message}</p>
         <p className="text-sm">No data available for the selected time period</p>
      </div>
   );

   return (
      <div className="space-y-6">
         {/* Summary Cards */}
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Applications */}
            <Card className="overflow-hidden shadow-md transition-all hover:shadow-lg">
               <CardHeader className="pb-2 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20">
                  <div className="flex justify-between items-center">
                     <CardTitle className="text-sm font-medium">Total Applications</CardTitle>
                     <FileText className="h-5 w-5 text-blue-600" />
                  </div>
               </CardHeader>
               <CardContent className="pt-4">
                  <div className="text-3xl font-bold text-blue-600">{totalApplications}</div>
                  <div className="flex items-center mt-2 text-xs">
                     <Badge variant="outline" className="mr-2">All Time</Badge>
                     <span className="text-muted-foreground">
                        Applications Tracked
                     </span>
                  </div>
               </CardContent>
            </Card>

            {/* Submitted Applications */}
            <Card className="overflow-hidden shadow-md transition-all hover:shadow-lg">
               <CardHeader className="pb-2 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20">
                  <div className="flex justify-between items-center">
                     <CardTitle className="text-sm font-medium">Submitted</CardTitle>
                     <ClipboardCheck className="h-5 w-5 text-green-600" />
                  </div>
               </CardHeader>
               <CardContent className="pt-4">
                  <div className="text-3xl font-bold text-green-600">{submittedCount}</div>
                  <div className="flex items-center justify-between mt-2">
                     <div className="w-2/3 bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                        <div className="bg-green-600 h-2.5 rounded-full" style={{ width: `${submittedPercent}%` }}></div>
                     </div>
                     <div className="text-xs text-green-600 font-medium">{submittedPercent}%</div>
                  </div>
               </CardContent>
            </Card>

            {/* Returned Applications */}
            <Card className="overflow-hidden shadow-md transition-all hover:shadow-lg">
               <CardHeader className="pb-2 bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20">
                  <div className="flex justify-between items-center">
                     <CardTitle className="text-sm font-medium">Returned</CardTitle>
                     <AlertTriangle className="h-5 w-5 text-red-600" />
                  </div>
               </CardHeader>
               <CardContent className="pt-4">
                  <div className="text-3xl font-bold text-red-600">{returnedCount}</div>
                  <div className="flex items-center justify-between mt-2">
                     <div className="w-2/3 bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                        <div className="bg-red-600 h-2.5 rounded-full" style={{ width: `${returnedPercent}%` }}></div>
                     </div>
                     <div className="text-xs text-red-600 font-medium">{returnedPercent}%</div>
                  </div>
               </CardContent>
            </Card>

            {/* In Progress Applications */}
            <Card className="overflow-hidden shadow-md transition-all hover:shadow-lg">
               <CardHeader className="pb-2 bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20">
                  <div className="flex justify-between items-center">
                     <CardTitle className="text-sm font-medium">In Progress</CardTitle>
                     <Clock className="h-5 w-5 text-amber-600" />
                  </div>
               </CardHeader>
               <CardContent className="pt-4">
                  <div className="text-3xl font-bold text-amber-600">{inProgressCount}</div>
                  <div className="flex items-center justify-between mt-2">
                     <div className="w-2/3 bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                        <div className="bg-amber-600 h-2.5 rounded-full" style={{ width: `${inProgressPercent}%` }}></div>
                     </div>
                     <div className="text-xs text-amber-600 font-medium">{inProgressPercent}%</div>
                  </div>
               </CardContent>
            </Card>
         </div>

         {/* Application Type and Status Distribution */}
         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="shadow-lg">
               <CardHeader>
                  <div className="flex justify-between items-center">
                     <div>
                        <CardTitle className="text-lg font-semibold">Applications by Type</CardTitle>
                        <CardDescription>Distribution across permit categories</CardDescription>
                     </div>
                     <BarChart className="h-5 w-5 text-muted-foreground" />
                  </div>
               </CardHeader>
               <CardContent className="h-[400px]">
                  {hasApplicationTypes ? (
                     <ResponsivePie
                        {...commonPieProps}
                        data={applicationTypes}
                        colors={{ scheme: 'nivo' }}
                        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                        arcLinkLabelsThickness={2}
                        arcLinkLabelsColor={{ from: 'color' }}
                        arcLabelsTextColor="#ffffff"
                        legends={[
                           {
                              anchor: 'bottom',
                              direction: 'row',
                              justify: false,
                              translateX: 0,
                              translateY: 56,
                              itemsSpacing: 20,
                              itemWidth: 100,
                              itemHeight: 18,
                              itemDirection: 'left-to-right',
                              itemOpacity: 1,
                              symbolSize: 18,
                              symbolShape: 'circle',
                              effects: [
                                 {
                                    on: 'hover',
                                    style: {
                                       itemTextColor: isDark ? '#ffffff' : '#000000'
                                    }
                                 }
                              ]
                           }
                        ]}
                     />
                  ) : (
                     <EmptyState message="No Application Types Data" />
                  )}
               </CardContent>
            </Card>

            <Card className="shadow-lg">
               <CardHeader>
                  <div className="flex justify-between items-center">
                     <div>
                        <CardTitle className="text-lg font-semibold">Application Status Distribution</CardTitle>
                        <CardDescription>Current state of all applications</CardDescription>
                     </div>
                     <CalendarDays className="h-5 w-5 text-muted-foreground" />
                  </div>
               </CardHeader>
               <CardContent className="h-[400px]">
                  {hasStatusData ? (
                     <ResponsivePie
                        {...commonPieProps}
                        data={statusData}
                        colors={({ id }) => {
                           if (id === 'Submitted') return '#22c55e'; // Green for Submitted
                           if (id === 'Returned') return '#ef4444'; // Red for Returned
                           if (id === 'In Progress') return '#f97316'; // Orange for In Progress
                           if (id === 'Completed') return '#3b82f6'; // Blue for Completed
                           // Default colors for any other statuses
                           return '#8b5cf6'; // Purple default
                        }}
                        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                        arcLinkLabelsThickness={2}
                        arcLinkLabelsColor={{ from: 'color' }}
                        arcLabelsTextColor="#ffffff"
                        legends={[
                           {
                              anchor: 'bottom',
                              direction: 'row',
                              translateY: 56,
                              itemWidth: 100,
                              itemHeight: 18,
                              symbolSize: 18,
                              symbolShape: 'circle'
                           }
                        ]}
                     />
                  ) : (
                     <EmptyState message="No Status Distribution Data" />
                  )}
               </CardContent>
            </Card>
         </div>

         {/* Weekly Volume and Processing Time */}
         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="shadow-lg">
               <CardHeader>
                  <div className="flex justify-between items-center">
                     <div>
                        <CardTitle className="text-lg font-semibold">Weekly Application Volume</CardTitle>
                        <CardDescription>Applications submitted by day</CardDescription>
                     </div>
                     <BarChart className="h-5 w-5 text-muted-foreground" />
                  </div>
               </CardHeader>
               <CardContent className="h-[400px]">
                  {hasWeeklyVolume ? (
                     <ResponsiveBar
                        data={weeklyVolume}
                        keys={['count']}
                        indexBy="day"
                        margin={{ top: 50, right: 50, bottom: 50, left: 60 }}
                        padding={0.3}
                        valueScale={{ type: 'linear' }}
                        indexScale={{ type: 'band', round: true }}
                        colors="#22c55e"
                        borderRadius={4}
                        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                        axisTop={null}
                        axisRight={null}
                        theme={{
                           axis: {
                              ticks: {
                                 text: {
                                    fill: isDark ? "#ffffff" : "#333333"
                                 }
                              },
                              legend: {
                                 text: {
                                    fill: isDark ? "#ffffff" : "#333333"
                                 }
                              }
                           },
                           grid: {
                              line: {
                                 stroke: isDark ? "#374151" : "#e5e7eb"
                              }
                           },
                           tooltip: {
                              container: {
                                 background: isDark ? "#1f2937" : "#ffffff",
                                 color: isDark ? "#ffffff" : "#333333",
                              }
                           }
                        }}
                        axisBottom={{
                           tickSize: 5,
                           tickPadding: 5,
                           tickRotation: 0,
                           legend: 'Day of Week',
                           legendPosition: 'middle',
                           legendOffset: 32
                        }}
                        axisLeft={{
                           tickSize: 5,
                           tickPadding: 5,
                           tickRotation: 0,
                           legend: 'Number of Applications',
                           legendPosition: 'middle',
                           legendOffset: -40
                        }}
                        labelSkipWidth={12}
                        labelSkipHeight={12}
                        labelTextColor={isDark ? "#ffffff" : "#000000"}
                        animate={true}
                        enableLabel={true}
                        isInteractive={true}
                        motionConfig="gentle"
                     />
                  ) : (
                     <EmptyState message="No Weekly Volume Data" />
                  )}
               </CardContent>
            </Card>

            <Card className="shadow-lg">
               <CardHeader>
                  <div className="flex justify-between items-center">
                     <div>
                        <CardTitle className="text-lg font-semibold">Application Processing Time</CardTitle>
                        <CardDescription>Average days by application type</CardDescription>
                     </div>
                     <Clock className="h-5 w-5 text-muted-foreground" />
                  </div>
               </CardHeader>
               <CardContent className="h-[400px]">
                  <ResponsiveBar
                     data={processingTimeData}
                     keys={['value']}
                     indexBy="id"
                     margin={{ top: 50, right: 50, bottom: 50, left: 60 }}
                     padding={0.3}
                     valueScale={{ type: 'linear' }}
                     indexScale={{ type: 'band', round: true }}
                     colors="#3b82f6"
                     borderRadius={4}
                     borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                     axisTop={null}
                     axisRight={null}
                     theme={{
                        axis: {
                           ticks: {
                              text: {
                                 fill: isDark ? "#ffffff" : "#333333"
                              }
                           },
                           legend: {
                              text: {
                                 fill: isDark ? "#ffffff" : "#333333"
                              }
                           }
                        },
                        grid: {
                           line: {
                              stroke: isDark ? "#374151" : "#e5e7eb"
                           }
                        },
                        tooltip: {
                           container: {
                              background: isDark ? "#1f2937" : "#ffffff",
                              color: isDark ? "#ffffff" : "#333333",
                           }
                        }
                     }}
                     axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Permit Type',
                        legendPosition: 'middle',
                        legendOffset: 32
                     }}
                     axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Days to Process',
                        legendPosition: 'middle',
                        legendOffset: -40
                     }}
                     labelSkipWidth={12}
                     labelSkipHeight={12}
                     labelTextColor={isDark ? "#ffffff" : "#000000"}
                     animate={true}
                     enableLabel={true}
                     label={d => `${d.value} days`}
                  />
               </CardContent>
            </Card>
         </div>

         {/* Application Trend */}
         <Card className="shadow-lg">
            <CardHeader>
               <div className="flex justify-between items-center">
                  <div>
                     <CardTitle className="text-lg font-semibold">Application Submission Trend</CardTitle>
                     <CardDescription>Weekly application volume over time</CardDescription>
                  </div>
                  <TrendingUp className="h-5 w-5 text-muted-foreground" />
               </div>
            </CardHeader>
            <CardContent className="h-[400px]">
               <ResponsiveLine
                  data={trendData}
                  margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
                  xScale={{ type: 'point' }}
                  yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false, reverse: false }}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                     tickSize: 5,
                     tickPadding: 5,
                     tickRotation: 0,
                     legend: 'Week',
                     legendOffset: 36,
                     legendPosition: 'middle'
                  }}
                  axisLeft={{
                     tickSize: 5,
                     tickPadding: 5,
                     tickRotation: 0,
                     legend: 'Total Applications',
                     legendOffset: -40,
                     legendPosition: 'middle'
                  }}
                  colors="#3b82f6"
                  pointSize={10}
                  pointColor="#ffffff"
                  pointBorderWidth={2}
                  pointBorderColor={{ from: 'serieColor' }}
                  pointLabelYOffset={-12}
                  useMesh={true}
                  legends={[
                     {
                        anchor: 'bottom-right',
                        direction: 'column',
                        justify: false,
                        translateX: 100,
                        translateY: 0,
                        itemsSpacing: 0,
                        itemDirection: 'left-to-right',
                        itemWidth: 80,
                        itemHeight: 20,
                        itemOpacity: 0.75,
                        symbolSize: 12,
                        symbolShape: 'circle',
                        symbolBorderColor: 'rgba(0, 0, 0, .5)',
                     }
                  ]}
                  theme={{
                     axis: {
                        domain: {
                           line: {
                              stroke: isDark ? "#64748b" : "#cbd5e1"
                           }
                        },
                        ticks: {
                           text: {
                              fill: isDark ? "#f1f5f9" : "#334155"
                           }
                        },
                        legend: {
                           text: {
                              fill: isDark ? "#f1f5f9" : "#334155"
                           }
                        }
                     },
                     grid: {
                        line: {
                           stroke: isDark ? "#334155" : "#e2e8f0"
                        }
                     },
                     tooltip: {
                        container: {
                           background: isDark ? "#1e293b" : "#ffffff",
                           color: isDark ? "#f1f5f9" : "#334155",
                        }
                     }
                  }}
               />
            </CardContent>
         </Card>

         {/* Insights Section */}
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Most Common Type */}
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 shadow-md">
               <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-sm">
                     <BadgeCheck className="h-4 w-4 text-blue-600" />
                     Most Common Application
                  </CardTitle>
               </CardHeader>
               <CardContent>
                  <h3 className="text-xl font-bold">{mostFrequentType?.label || "No Data"}</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                     {mostFrequentType ? `${mostFrequentType.value} applications (${Math.round((mostFrequentType.value / totalApplications) * 100)}%)` : ""}
                  </p>
               </CardContent>
            </Card>

            {/* Weekly Change */}
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 shadow-md">
               <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-sm">
                     <ArrowUp className="h-4 w-4 text-green-600" />
                     Weekly Growth
                  </CardTitle>
               </CardHeader>
               <CardContent>
                  <h3 className="text-xl font-bold">+12%</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                     Compared to previous week
                  </p>
               </CardContent>
            </Card>

            {/* Completion Rate */}
            <Card className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 shadow-md">
               <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-sm">
                     <CheckCheck className="h-4 w-4 text-purple-600" />
                     Completion Rate
                  </CardTitle>
               </CardHeader>
               <CardContent>
                  <h3 className="text-xl font-bold">{completedPercent}%</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                     Applications fully processed
                  </p>
               </CardContent>
            </Card>
         </div>
      </div>
   );
};

export default ApplicationAnalytics;
